import React, { PropsWithChildren } from "react";
import { Link, useLocation } from "react-router-dom";
import { Spinner } from "app/components/Loading";
import { RootState } from "typedefs";
import Styles from "./styles.module.scss";
import edufii_logo_dark from "app/images/Logo_dark.svg";
// The wordmark in white, for the "Powered by" over a banner.
import edufii_logo_light from "app/images/Logo.svg";
import { shallowEqual, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import {
  hasFacilityBanner,
  hasFacilityLogo,
} from "app/components/PublicProfiles/Facility/facilityBranding";
import { facilitySummary } from "app/routes/Sessions/AllCoachesPublicSession/_components/coachMeta";

type PropsDef = PropsWithChildren<{
  isLoading?: boolean;
}>;

export default function PublicWrapper(props: PropsDef) {
  const location = useLocation();
  let content = props.children;
  if (props.isLoading) {
    content = <Spinner />;
  }
  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );
  const facility = useSelector(
    (state: RootState) => state.bookSession.facility,
  ) as any;
  const name: string = facility?.name ?? "";
  const { t } = useTranslation();

  /**
   * CN-2652: the public frames put the facility's own branding in this strip, and
   * the Landing Page Logo — `public_logo_url` — is the only thing that belongs
   * there.
   *
   * It used to fall back to `logo_url`, which is the facility's *email* logo: a
   * facility that had never set a landing page logo got that one on its banner
   * instead, and with it the facility name the banner is meant to carry itself.
   * An empty Landing Page Logo now means an empty slot, which is what the
   * banner-and-no-logo mock draws (518:14976).
   */
  const logo = hasFacilityLogo(facility?.public_logo_url)
    ? facility.public_logo_url
    : undefined;
  // With a banner the strip becomes the frame's full-bleed masthead: the cover
  // behind the logo, name and "Powered by CoachNow" instead of a white bar.
  const banner = hasFacilityBanner(facility?.public_cover_url)
    ? facility.public_cover_url
    : null;
  const isPublicFacilityPage = location.pathname.startsWith("/public");
  const showBanner = isPublicFacilityPage && Boolean(banner);
  /**
   * What the strip carries, by the branding matrix the landing page was built to
   * (CN-2652 phase 1) — chosen by which of the facility's two public images exist,
   * never by inventing one:
   *
   *   banner + logo    logo beside the name
   *   banner, no logo  nothing here; the banner is the facility's mark and the
   *                    landing page writes the name directly beneath it
   *   logo, no banner  logo beside the name
   *   neither          the name alone
   *
   * CFTB-3427 removed an initials circle that stood in for a logo the facility had
   * never uploaded, and took the last row with it by mistake — leaving a facility
   * with no images with nothing at all, which CFTB-3432 then found. The name comes
   * back when there is no banner to carry the facility instead.
   */
  const showFacilityIdentity = Boolean(logo) || !banner;

  /**
   * CFTB-3449: "6 coaches · 24 session types" under the facility's name, as the
   * frame writes it.
   *
   * No new request for it: the payload this page already loads
   * (`/api/public/facilities/:uuid`) carries every coach with their bookable rates,
   * and `facilitySummary` is the same helper the in-app booking page counts with —
   * so both screens can never disagree.
   *
   * Only the facility's own page has coaches to count. A coach's public link loads
   * the facility nested in the coach, which is the shorter payload with no coaches
   * in it, and that frame writes no counts either — so a zero count draws nothing
   * rather than "0 coaches".
   */
  /**
   * Counted over the coaches a visitor can actually open, which is what the
   * list below shows: the payload carries every coach whatever their setting,
   * and one with their public calendar off cannot be booked here at all — so
   * counting them said "5 coaches" over a list of three.
   */
  const facilityCoaches = (facility?.coachProfiles || []).filter(
    (coach) => coach.publicCalendar !== false,
  );
  const { coachCount, sessionTypeCount } = facilitySummary(facilityCoaches);

  return (
    <div
      className={cls(
        Styles.publicWrapper,
        showBanner && Styles.withBanner,
        // Global, so `#root` can let the document scroll for this page — see the
        // rule beside the public profile pages' own in main.scss.
        showBanner && "public-banner-page",
        // The strip has to be tall enough for the logo in it — see the note on
        // `.with-facility-logo`. Over a banner the masthead sizes itself.
        !showBanner && Boolean(logo) && Styles.withFacilityLogo,
      )}
    >
      <div className={Styles.navbar}>
        {/*
          An image in the flow rather than a cover on a fixed band: a band crops
          whatever does not fit, and these covers are made *with* the facility's
          name in them — cropping took the name off the page at anything but one
          window width. Its height follows its own proportions, up to the band the
          frame draws, so a wide cover is never cropped and a nearly-square one
          does not take the whole window.
        */}
        {showBanner && (
          <img className={Styles.banner_image} src={banner} alt="" />
        )}
        <div className={Styles.navbarContent}>
          {isPublicFacilityPage ? (
            <div
              className={cls(
                Styles.header_container,
                // Nothing on the left to space against, and both frames keep
                // "Powered by CoachNow" at the right rather than sliding it over.
                !showFacilityIdentity && Styles.header_container_end,
              )}
            >
              {showFacilityIdentity && (
                <div className={Styles.facility_logo}>
                  {logo && <img src={logo} alt="logo" />}
                  {/* The counts sit inside the name rather than beside it: every
                      rule this strip has for the facility's name is written as a
                      direct child of `.facility_logo`, including the white it takes
                      over a banner, and a wrapper would step out of all of them. */}
                  <p>
                    {name}
                    {coachCount > 0 && (
                      <span className={Styles.facility_counts}>
                        {t("{{count}} coaches", { count: coachCount })}
                        {" · "}
                        {t("{{count}} session types", {
                          count: sessionTypeCount,
                        })}
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div className={Styles.left_container}>
                <div className={Styles.logo_container}>
                  <p>{t("Powered by")}</p>
                  <span>
                    <Link to="/">
                      {/* White over a picture, dark on the light strip. */}
                      <img
                        src={showBanner ? edufii_logo_light : edufii_logo_dark}
                        alt="CoachNow"
                      />
                    </Link>
                  </span>
                </div>
                {/*
                  No "Sign in" here: the frames hide it (`Sign In` is a hidden node
                  in 526:50845), and a visitor books without an account — the flow
                  asks who they are when it needs to know.
                */}
                {user !== null && (
                  <div className={Styles.user_container}>
                    <div className={Styles.user_name}>{user.name}</div>
                    <img className={Styles.avatar} src={user.avatar} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <span className={Styles.logo}>
              <Link to="/">
                <img src={edufii_logo_dark} alt="CoachNow" />
              </Link>
            </span>
          )}
        </div>
      </div>

      <div className={Styles.content}>
        <div>{content}</div>
      </div>
    </div>
  );
}
